import { useEffect, useState } from "react";
import { ShieldAlert } from "lucide-react";
import { declarationsForBooking, publicDeclarations, declarationsNotice, publicDeclarationsNotice,
         DEFAULT_DECLARATIONS_NOTICE } from "@/lib/api";

// Active declarations for a site + direction (signed-in form) or a public link slug.
export function useDeclarations({ siteId, direction, slug }) {
  const [list, setList] = useState([]);
  useEffect(() => {
    if (!slug && !siteId) { setList([]); return undefined; }
    let cancelled = false;
    (slug ? publicDeclarations(slug, direction) : declarationsForBooking(siteId, direction))
      .then(({ data, error }) => { if (!cancelled) setList(error ? [] : data || []); });
    return () => { cancelled = true; };
  }, [siteId, direction, slug]);
  return list;
}

// "Must be confirmed before site/bay access" wording (site > tenant > standard).
export function useDeclarationsNotice({ siteId, slug }) {
  const [notice, setNotice] = useState(DEFAULT_DECLARATIONS_NOTICE);
  useEffect(() => {
    if (!slug && !siteId) return undefined;
    let cancelled = false;
    (slug ? publicDeclarationsNotice(slug) : declarationsNotice(siteId))
      .then(({ data, error }) => { if (!cancelled) setNotice((!error && data) || DEFAULT_DECLARATIONS_NOTICE); });
    return () => { cancelled = true; };
  }, [siteId, slug]);
  return notice;
}

export const ArrivalNotice = ({ notice, testId }) => (
  <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
       data-testid={testId}>
    <ShieldAlert className="h-4 w-4 shrink-0 text-amber-600 mt-0.5" />
    <span>{notice}</span>
  </div>
);

export function DeclarationsList({ list, answers, onChange, notice, testPrefix = "declaration" }) {
  if (!list.length) return null;
  const anyRequired = list.some((d) => d.is_required);
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-2" data-testid={`${testPrefix}s-block`}>
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Declarations</div>
      {anyRequired && notice && <ArrivalNotice notice={notice} testId={`${testPrefix}s-notice`} />}
      {list.map((d) => {
        const pending = d.is_required && !answers[d.id];
        return (
          <label key={d.id} className="flex items-start gap-2 text-sm cursor-pointer">
            <input type="checkbox" className="mt-0.5 h-4 w-4 accent-slate-900"
                   checked={!!answers[d.id]}
                   onChange={(e) => onChange({ ...answers, [d.id]: e.target.checked })}
                   data-testid={`${testPrefix}-${d.id}`} />
            <span>
              {d.statement}
              {d.is_required && <span className="ml-1 text-rose-600" title="Mandatory for site access">*</span>}
              {pending && (
                <span className="ml-2 inline-block rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800"
                      data-testid={`${testPrefix}-pending-${d.id}`}>
                  To confirm at arrival
                </span>
              )}
            </span>
          </label>
        );
      })}
    </div>
  );
}

// Staff view of a saved booking snapshot — flags required items not yet confirmed.
export function DeclarationsSnapshot({ snapshot, testId = "booking-declarations" }) {
  const items = (Array.isArray(snapshot) ? snapshot : []);
  if (!items.length) return null;
  return (
    <ul className="space-y-1 text-sm" data-testid={testId}>
      {items.map((d) => (
        <li key={d.id} className="flex items-start gap-2" data-testid={`${testId}-${d.id}`}>
          <span className={d.answer ? "text-emerald-600" : "text-rose-600"}>{d.answer ? "✓" : "✗"}</span>
          <span>
            {d.statement}{d.required && <span className="text-rose-600"> *</span>}
            {d.required && !d.answer && (
              <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide text-amber-700">To confirm at arrival</span>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}
